import { Building2, ChevronDown, LogOut, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { RequireRole } from '@/app/guards/RequireRole';
import { useTenant } from '@/app/providers/TenantContextProvider';
import { usePrincipal } from '@/core/auth/appToken';
import { useAuthAccount } from '@/core/auth/authHooks';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { ActiveLabSubmenu } from './ActiveLabSubmenu';
import { organizationPersonaKey, type OrganizationType } from '@/models/organizationType';

/** Organisation types whose members may see their own organisation's contact data. */
const CUSTOMER_ORG_TYPES: ReadonlySet<OrganizationType> = new Set([
  'PRACTICE',
  'CUSTOMER_LAB',
]);

/**
 * Chrome-agnostic user menu — icon-only `Avatar` trigger (embedded into `AppHeader` in a later
 * work-item) opening a shadcn `DropdownMenu`. Menu entries:
 * - "Benutzerprofil" and "Logout" — every authenticated role.
 * - "Meine Organisation" (the organisation's own self-service master data) — only when the
 *   active organisation is a customer org ({@link CUSTOMER_ORG_TYPES}) **and** the user's role
 *   tier is CEO or LEAD (`RequireRole tier="LEAD"` already covers CEO via the tier hierarchy).
 *   This is client-side UX gating only — the real self-service endpoint authorizes
 *   independently server-side (E01-03 Security-Hotspots); read access there is wider (any org
 *   member), the write path narrower (GF/Leitung only), which is why this menu entry itself
 *   stays gated to LEAD-and-above only.
 * - "Mein Laborprofil" (the DELABO laboratory's own self-service master data) — only when the
 *   active organisation type is `DELABO_LAB`, for **all three** laboratory role tiers
 *   (CEO/LEAD/EMPLOYEE), deliberately without a `RequireRole` wrapper: `GET
 *   .../laborprofil` is readable by every laboratory role, only the `PUT`/logo writes are
 *   GF/Leitung-only, and the page itself renders the read-only view for `DELABO_LAB_EMPLOYEE`
 *   (same read-vs-write split as "Meine Organisation", just with a wider read audience).
 *
 * There is deliberately **no** "Lieferdienstverwaltung" entry here (US-E15-01) — it moved out of
 * this dropdown once the top-nav "Logistik" item started pointing at the real Logistik area
 * screen (`/logistik`) for `DELABO_LAB` principals (work-item 15, N17/N18): from there, the
 * "Versanddienstleister verwalten" button is the sole jump-off to
 * `/logistik/lieferdienstverwaltung`. Keeping a second entry here would have been a redundant,
 * now-orphaned path to the same page.
 *
 * The displayed name comes from {@link useAuthAccount} (`Principal.userDisplayName`) and is
 * rendered directly in the trigger (Pencil node `d2jH0`, "User Menu": Avatar + name + chevron),
 * not only inside the opened dropdown. Below the name, the trigger shows the active laboratory's
 * name for DELABO-Labor users, otherwise the active organization's persona label
 * ({@link organizationPersonaKey}), in muted gray — the same visual treatment as
 * {@link PartnerLabInfo}'s contact line — no numeric/tier role label is shown (deferred to #352).
 * The Avatar shows the active laboratory's logo when one exists (there is no profile picture) and
 * is squared (`rounded-md`, which resolves to 0 under this theme's `--radius: 0rem`),
 * not circular — matching {@link PartnerLabInfo}'s logo-placeholder box for a consistent header
 * silhouette.
 */
export function UserMenu() {
  const { t } = useTranslation('app');
  const { t: tOrgType } = useTranslation('organizationType');
  const account = useAuthAccount();
  const tenant = useTenant();
  const activeLab = usePrincipal()?.selectableOrganizations.find((o) => o.active);
  const handleLogout = useLogout();

  const displayName = account?.displayName ?? '';
  const subtitle = activeLab?.name ?? tOrgType(organizationPersonaKey(tenant?.organizationType));
  const isCustomerOrg = tenant !== null && CUSTOMER_ORG_TYPES.has(tenant.organizationType);
  const isDelaboLab = tenant?.organizationType === 'DELABO_LAB';

  const handleLogoutSelect = (): void => {
    handleLogout();
    toast.success(t('portal.header.loggedOut'));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto gap-2 rounded-md px-2 py-1"
          aria-label={t('portal.header.userMenu')}
        >
          <Avatar className="rounded-md">
            {activeLab?.logoUrl ? (
              <AvatarImage
                src={activeLab.logoUrl}
                alt={t('portal.header.activeLab.logoAlt', { name: activeLab.name })}
                className="object-contain"
              />
            ) : null}
            <AvatarFallback className="rounded-md">
              <UserRound className="h-4 w-4" aria-hidden="true" />
            </AvatarFallback>
          </Avatar>
          {displayName.length > 0 && (
            <span className="hidden min-w-0 max-w-60 flex-col items-start text-left sm:flex">
              <span className="w-full truncate text-sm font-medium text-foreground">{displayName}</span>
              <span className="w-full truncate text-xs text-muted-foreground" title={subtitle}>
                {subtitle}
              </span>
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {displayName.length > 0 && (
          <>
            <DropdownMenuLabel>{displayName}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}

        <ActiveLabSubmenu />

        <DropdownMenuItem asChild>
          <Link to="/profil">
            <UserRound aria-hidden="true" />
            {t('portal.header.profile')}
          </Link>
        </DropdownMenuItem>

        {isCustomerOrg ? (
          <RequireRole tier="LEAD">
            <DropdownMenuItem asChild>
              <Link to="/meine-organisation">
                <Building2 aria-hidden="true" />
                {t('portal.header.myOrganization')}
              </Link>
            </DropdownMenuItem>
          </RequireRole>
        ) : null}

        {isDelaboLab ? (
          <DropdownMenuItem asChild>
            <Link to="/mein-laborprofil">
              <Building2 aria-hidden="true" />
              {t('portal.header.myLabProfile')}
            </Link>
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={handleLogoutSelect}
        >
          <LogOut aria-hidden="true" />
          {t('portal.header.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
